import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Battery, Loader2, RefreshCw, BellRing, MapPin, ClipboardList } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartData,
  ChartOptions,
  ChartDataset
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chart.js/auto';
import { useGetSensorListQuery, useGetUserPreferencesQuery, useUpsertUserPreferenceMutation } from '../services/sensorApi';
import { useGetSensorReadingsQuery, useGetLiveDataQuery } from '../services/sensorDataApi';
import { formatSensorSerial } from '../utils/sensorUtils';
import { getChartOptions } from '../utils/chartConfig';
import {
  smoothTemperatureData,
  smoothBatteryData,
  isLikelyOneDecimalTemperatures,
  adaptivePointRadius,
  makeGapSegmentStyle,
  buildGapMarkers,
  inferGapThresholdMs,
  formatGapDuration,
} from '../utils/chartUtils';
import { adjustTimestampForDisplay, adjustDateForAPI } from '../utils/dateUtils';
import 'chartjs-adapter-date-fns';
import { format as formatFns } from 'date-fns';
import { useGetAlarmsQuery, useGetAlarmLogQuery, useAcknowledgeAlarmLogMutation, useUpdateSensorAlarmMutation, alarmApi } from '../services/alarmApi';
import { AlarmAcknowledgeDialog } from '../components/alarms/AlarmAcknowledgeDialog';
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLogger';
import { normalizeAlarmType, alarmTypeShortLabel, formatAlarmReading } from '../utils/alarmDisplay';
import { filterAlarmLogsBySensorAccess } from '../utils/alarmAccess';
import { SplitChannelDialog } from '../components/sensors/SplitChannelDialog';
import { expandSensors, isSplitRow } from '../utils/expandSensors';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin,
  annotationPlugin
);

const timeRanges = [
  { label: '24 Hours', hours: 24 },
  { label: '7 Days', hours: 168 },
  { label: '30 Days', hours: 720 },
  { label: '60 Days', hours: 1440 },
  { label: 'All Data', hours: Infinity },
  { label: 'Custom Range', hours: 'custom' }
];

type TimeRange = {
  label: string;
  hours: number | 'custom';
};

type TimeSeriesData = {
  x: Date;
  y: number;
};

const ONE_MIN_MS = 60 * 1000;

function isValidDate(d: unknown): d is Date {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** `input type="date"` value in local calendar. */
function toDateInputValue(d: Date): string {
  if (!isValidDate(d)) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** `input type="time"` value (minute resolution). */
function toTimeInputValue(d: Date): string {
  if (!isValidDate(d)) return '';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function sameLocalCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function combineLocalDateTime(dateStr: string, timeStr: string): Date | null {
  if (!dateStr) return null;
  const t = timeStr && timeStr.length >= 4 ? timeStr : '00:00';
  const d = new Date(`${dateStr}T${t}`);
  return isValidDate(d) ? d : null;
}

export default function SensorDetailsPage() {
  const { sensorId } = useParams<{ sensorId: string }>();
  const [searchParams] = useSearchParams();
  const channelParam = searchParams.get('channel');
  const activeChannel: 1 | 2 | null = channelParam === '2' ? 2 : channelParam === '1' ? 1 : null;
  const fromAlarmLog = searchParams.get('from') === 'alarm-log';
  const navigate = useNavigate();
  const chartRef = useRef<ChartJS<'line', TimeSeriesData[]>>(null);
  const zoomStateRef = useRef<any>(null); // Store zoom state
  const zoomRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedRange, setSelectedRange] = useState<TimeRange>(timeRanges[0]);
  const [showBattery, setShowBattery] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 24 * 60 * 60 * 1000),
    end: new Date()
  });
  const [customDateRange, setCustomDateRange] = useState({
    start: new Date(Date.now() - 24 * 60 * 60 * 1000),
    end: new Date()
  });
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isChartReady, setIsChartReady] = useState(false);
  const [isRangeChanging, setIsRangeChanging] = useState(false);
  const [showTemp1, setShowTemp1] = useState(true);
  const [showTemp2, setShowTemp2] = useState(true);
  const [showTemperature, setShowTemperature] = useState(true);
  const [showRSSI, setShowRSSI] = useState(false);
  const [showSNR, setShowSNR] = useState(false);
  const [showHighAlarms, setShowHighAlarms] = useState(true);
  const [showLowAlarms, setShowLowAlarms] = useState(true);
  const [alarmPoints, setAlarmPoints] = useState<any[]>([]);
  const xAxisRangeRef = useRef<{min: number, max: number} | null>(null);
  const [xRange, setXRange] = useState<{min: number|undefined, max: number|undefined}>({min: undefined, max: undefined});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showScalePanel, setShowScalePanel] = useState(false);
  const [scaleInputs, setScaleInputs] = useState({ yMin: '', yMax: '' });
  const [overlaySensorId, setOverlaySensorId] = useState<number | null>(null);
  const [overlaySensorChannel, setOverlaySensorChannel] = useState<1 | 2 | null>(null);
  const [showOverlayPicker, setShowOverlayPicker] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const [acknowledgeAlarmLog] = useAcknowledgeAlarmLogMutation();
  const userId = user?.id != null ? Number(user.id) : undefined;
  const { data: userPreferences } = useGetUserPreferencesQuery(
    { userId: userId! },
    { skip: !userId }
  );
  const [upsertPreference] = useUpsertUserPreferenceMutation();
  const [updateSensorAlarm] = useUpdateSensorAlarmMutation();
  const [showSplitChannelDialog, setShowSplitChannelDialog] = useState(false);
  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [ackNoteDraft, setAckNoteDraft] = useState('');
  const [ackSubmitting, setAckSubmitting] = useState(false);
  const [ackError, setAckError] = useState<string | null>(null);
  const [showThresholdSliders, setShowThresholdSliders] = useState(false);
  const [draftHigh, setDraftHigh] = useState<number | null>(null);
  const [draftLow, setDraftLow] = useState<number | null>(null);
  const [thresholdSaveStatus, setThresholdSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const userDisplayName = user?.email || user?.firstName || (user?.id != null ? String(user.id) : '') || 'system';
  const { data: sensors } = useGetSensorListQuery();
  const { data: readings, isLoading } = useGetSensorReadingsQuery({
    sensorId: Number(sensorId),
    startDate: adjustDateForAPI(dateRange.start).toISOString(),
    endDate: adjustDateForAPI(dateRange.end).toISOString(),
    ...(activeChannel != null ? { channel: activeChannel } : {}),
  }, {
    pollingInterval: 0, // Disable automatic polling
    refetchOnMountOrArgChange: false, // Disable automatic refetching
    refetchOnReconnect: false, // Disable refetch on reconnect
    refetchOnFocus: false, // Disable refetch on window focus
    // Keep chart mounted during pinch-zoom refetches into a narrower window
    placeholderData: (previousData) => previousData,
  });
  const { data: overlayReadings } = useGetSensorReadingsQuery({
    sensorId: overlaySensorId!,
    startDate: adjustDateForAPI(dateRange.start).toISOString(),
    endDate: adjustDateForAPI(dateRange.end).toISOString(),
    ...(overlaySensorChannel != null ? { channel: overlaySensorChannel } : {}),
  }, {
    skip: !overlaySensorId,
    pollingInterval: 0,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: false,
    refetchOnFocus: false,
    placeholderData: (previousData) => previousData,
  });
  const { data: alarms } = useGetAlarmsQuery();
  const { data: alarmLogRaw } = useGetAlarmLogQuery({ includeAcknowledged: true }, { skip: !user });
  const { data: activeAlarmLogRaw } = useGetAlarmLogQuery(undefined, { skip: !user });
  const alarmLog = useMemo(
    () => filterAlarmLogsBySensorAccess(alarmLogRaw, sensors),
    [alarmLogRaw, sensors],
  );
  const activeAlarmLog = useMemo(
    () => filterAlarmLogsBySensorAccess(activeAlarmLogRaw, sensors),
    [activeAlarmLogRaw, sensors],
  );
  const [shouldFetchLiveData, setShouldFetchLiveData] = useState(false);
  const [liveData, setLiveData] = useState<any[]>([]);
  const { data: fetchedLiveData, refetch: refetchLiveData } = useGetLiveDataQuery(undefined, {
    pollingInterval: 0, // Disable automatic polling
    refetchOnMountOrArgChange: false, // Disable automatic refetching
    refetchOnReconnect: false,
    refetchOnFocus: false, // Disable refetch on window focus
    skip: !shouldFetchLiveData, // Skip the query entirely until we need it
  });

  const temperature1Values = useMemo(
    () =>
      readings
        ?.filter(
          (r) =>
            r.temperature !== null && r.temperature !== 8001 && r.temperature !== 8002
        )
        .map((r) => r.temperature as number) ?? [],
    [readings]
  );
  const temperature2Values = useMemo(
    () =>
      readings
        ?.filter(
          (r) =>
            typeof r.temperature_2 === 'number' &&
            r.temperature_2 !== null &&
            !isNaN(r.temperature_2)
        )
        .map((r) => r.temperature_2 as number) ?? [],
    [readings]
  );
  const temp1ChartTension = useMemo(
    () => (isLikelyOneDecimalTemperatures(temperature1Values) ? 0.42 : 0.3),
    [temperature1Values]
  );
  const temp2ChartTension = useMemo(
    () => (isLikelyOneDecimalTemperatures(temperature2Values) ? 0.42 : 0.3),
    [temperature2Values]
  );

  const sensor = sensors?.find(s => s.id === Number(sensorId));

  // Prev/next graph navigation, following the same order as the dashboard table:
  // the saved drag-drop order (localStorage 'sensorOrder', synced from user
  // preferences by SensorTable), with unordered sensors after, in API order.
  const orderedSensorIds = useMemo(() => {
    const list = (sensors ?? []).filter(s => !s.is_deleted);
    let orderMap = new Map<string, number>();
    try {
      const saved = localStorage.getItem('sensorOrder');
      if (saved) orderMap = new Map<string, number>(JSON.parse(saved));
    } catch { /* corrupt saved order — fall back to API order */ }
    return list
      .map(s => ({ id: s.id, order: orderMap.get(s.id.toString()) ?? Number.MAX_SAFE_INTEGER }))
      .sort((a, b) => a.order - b.order)
      .map(x => x.id);
  }, [sensors]);
  const sensorNavIndex = orderedSensorIds.indexOf(Number(sensorId));
  const prevSensorId = sensorNavIndex >= 0 && orderedSensorIds.length > 1
    ? orderedSensorIds[(sensorNavIndex - 1 + orderedSensorIds.length) % orderedSensorIds.length]
    : null;
  const nextSensorId = sensorNavIndex >= 0 && orderedSensorIds.length > 1
    ? orderedSensorIds[(sensorNavIndex + 1) % orderedSensorIds.length]
    : null;

  // Per-channel config (when the sensor has been split)
  const channelConfig = activeChannel != null
    ? sensor?.channels?.find(c => c.channel === activeChannel) ?? null
    : null;
  const displayName = channelConfig ? channelConfig.display_name : sensor?.name ?? '';

  const overlaySensor = overlaySensorId ? (sensors?.find(s => s.id === overlaySensorId) ?? null) : null;
  const overlaySensorDisplayName = overlaySensor
    ? (overlaySensorChannel != null
        ? (overlaySensor.channels?.find(c => c.channel === overlaySensorChannel)?.display_name
            || `${overlaySensor.name} (Ch ${overlaySensorChannel})`)
        : overlaySensor.name)
    : null;

  const storedScale = useMemo(() => {
    const pref = userPreferences?.find(p => p.preference_key === `graph_scale_${sensorId}`);
    if (!pref?.preference_value) return null;
    try {
      const parsed = JSON.parse(pref.preference_value) as { yMin: number; yMax: number };
      if (typeof parsed.yMin === 'number' && typeof parsed.yMax === 'number') return parsed;
      return null;
    } catch { return null; }
  }, [userPreferences, sensorId]);

  useEffect(() => {
    if (storedScale) {
      setScaleInputs({ yMin: String(storedScale.yMin), yMax: String(storedScale.yMax) });
    } else {
      setScaleInputs({ yMin: '', yMax: '' });
    }
  }, [storedScale]);

  if (!sensor) {
    return (
      <div className="p-6">
        <div className="text-red-600">Sensor not found</div>
      </div>
    );
  }

  const alarm = alarms?.find((a: any) => a.sensor_id === sensor?.id);
  const rawSetpoints = channelConfig
    ? { high: channelConfig.high_alarm, low: channelConfig.low_alarm }
    : { high: (alarm as any)?.high_alarm, low: (alarm as any)?.low_alarm };
  // API may return alarm thresholds as strings; coerce to numbers so toFixed/Math.round work downstream
  const alarmSetpoints = {
    high: rawSetpoints.high != null && rawSetpoints.high !== '' ? Number(rawSetpoints.high) : null,
    low: rawSetpoints.low != null && rawSetpoints.low !== '' ? Number(rawSetpoints.low) : null,
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const sliderRange = useMemo(() => {
    const allTemps = [...temperature1Values, ...temperature2Values];
    const knownPoints = [
      ...allTemps,
      alarmSetpoints.high,
      alarmSetpoints.low,
    ].filter((v): v is number => v != null && isFinite(v));
    if (knownPoints.length === 0) return { min: -30, max: 60 };
    const dataMin = Math.min(...knownPoints);
    const dataMax = Math.max(...knownPoints);
    const headroom = Math.max((dataMax - dataMin) * 0.3, 15);
    return { min: Math.floor(dataMin - headroom), max: Math.ceil(dataMax + headroom) };
  }, [temperature1Values, temperature2Values, alarmSetpoints.high, alarmSetpoints.low]);

  const activeAlarm = (activeAlarmLog ?? alarmLog)?.find(
    (log: any) => Number(log.sensor_id) === sensor.id && log.active && !log.acknowledged
  ) ?? null;

  // Find the latest live data for this sensor
  const liveSensor = liveData?.find(ld => ld.sensor_id === sensor.id.toString());
  // Find the latest timestamp in readings
  const lastReadingTimestamp = readings && readings.length > 0 ? new Date(readings[readings.length - 1].timestamp) : null;
  // Only overlay if live data is newer and we have live data
  const shouldOverlayLive = liveData && liveSensor && liveSensor.timestamp && (!lastReadingTimestamp || new Date(liveSensor.timestamp) > lastReadingTimestamp);

  // Cleanup chart instance and timers on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
      if (zoomRefetchTimerRef.current) {
        clearTimeout(zoomRefetchTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selectedRange.hours === 'custom') {
      const { start, end } = customDateRange;
      if (isValidDate(start) && isValidDate(end) && start.getTime() < end.getTime()) {
        setDateRange({ start, end });
      }
      setShowCustomDateInputs(true);
    } else if (typeof selectedRange.hours === 'number') {
      setDateRange({
        start: selectedRange.hours === Infinity 
          ? new Date(0) // Start from epoch for all data
          : new Date(Date.now() - selectedRange.hours * 60 * 60 * 1000),
        end: new Date()
      });
      setShowCustomDateInputs(false);
    }
  }, [selectedRange, customDateRange]);

  // Set chart ready state when data is available
  useEffect(() => {
    if (readings && !isLoading) {
      // Check if we have any valid data points
      const hasValidData = readings.some(reading => 
        (reading.temperature !== null && reading.temperature !== 8001 && reading.temperature !== 8002) ||
        (reading.temperature_2 !== null && !isNaN(reading.temperature_2)) ||
        (reading.hum !== null && reading.hum !== undefined) ||
        (reading.battery >= 0.5)
      );
      
      setIsChartReady(hasValidData);
      setIsRangeChanging(false);
    }
  }, [readings, isLoading]);

  // Save zoom state before data changes
  useEffect(() => {
    if (chartRef.current && typeof (chartRef.current as any).getZoom === 'function') {
      zoomStateRef.current = (chartRef.current as any).getZoom();
    }
    // eslint-disable-next-line
  }, [readings]);

  // Restore zoom state after chart updates (but not for live data updates)
  useEffect(() => {
    if (chartRef.current && zoomStateRef.current && typeof (chartRef.current as any).zoom === 'function') {
      (chartRef.current as any).zoom(zoomStateRef.current);
    }
    // eslint-disable-next-line
  }, [isChartReady, readings]);

  // Update local liveData when fetched data changes
  useEffect(() => {
    if (fetchedLiveData && shouldFetchLiveData) {
      setLiveData(fetchedLiveData);
    }
  }, [fetchedLiveData, shouldFetchLiveData]);

  const moveTimeWindow = (direction: 'forward' | 'backward') => {
    if (selectedRange.hours === Infinity || selectedRange.hours === 'custom' || typeof selectedRange.hours !== 'number') return; // Don't allow moving window for all data or custom range
    
    const timeWindow = selectedRange.hours * 60 * 60 * 1000;
    setDateRange(prev => {
      const offset = direction === 'forward' ? timeWindow : -timeWindow;
      return {
        start: new Date(prev.start.getTime() + offset),
        end: new Date(prev.end.getTime() + offset)
      };
    });
  };

  const handleRangeChange = (range: TimeRange) => {
    setIsRangeChanging(true);
    setSelectedRange(range);
    // Always recompute dateRange directly — if same range clicked again,
    // selectedRange won't change so the useEffect won't fire, leaving
    // isRangeChanging stuck on true.
    if (range.hours !== 'custom' && typeof range.hours === 'number') {
      setDateRange({
        start: range.hours === Infinity
          ? new Date(0)
          : new Date(Date.now() - range.hours * 60 * 60 * 1000),
        end: new Date(),
      });
    }
  };

  const applyCustomStart = useCallback((nextStart: Date) => {
    if (!isValidDate(nextStart)) return;
    setCustomDateRange((prev) => {
      let nextEnd = prev.end;
      if (!isValidDate(nextEnd)) nextEnd = new Date();
      if (nextStart.getTime() >= nextEnd.getTime()) {
        nextEnd = new Date(nextStart.getTime() + ONE_MIN_MS);
      }
      return { start: nextStart, end: nextEnd };
    });
  }, []);

  const applyCustomEnd = useCallback((nextEnd: Date) => {
    if (!isValidDate(nextEnd)) return;
    setCustomDateRange((prev) => {
      let nextStart = prev.start;
      if (!isValidDate(nextStart)) {
        nextStart = new Date(Date.now() - 24 * 60 * 60 * 1000);
      }
      if (nextEnd.getTime() <= nextStart.getTime()) {
        nextStart = new Date(nextEnd.getTime() - ONE_MIN_MS);
      }
      return { start: nextStart, end: nextEnd };
    });
  }, []);



  const handleApplyScale = useCallback(async () => {
    if (!userId) return;
    const yMin = parseFloat(scaleInputs.yMin);
    const yMax = parseFloat(scaleInputs.yMax);
    if (isNaN(yMin) || isNaN(yMax) || yMin >= yMax) return;
    await upsertPreference({
      userId,
      preference_key: `graph_scale_${sensorId}`,
      preference_value: JSON.stringify({ yMin, yMax }),
    });
    setShowScalePanel(false);
  }, [userId, scaleInputs, sensorId, upsertPreference]);

  const handleClearScale = useCallback(async () => {
    if (!userId) return;
    await upsertPreference({
      userId,
      preference_key: `graph_scale_${sensorId}`,
      preference_value: '',
    });
    setScaleInputs({ yMin: '', yMax: '' });
    setShowScalePanel(false);
  }, [userId, sensorId, upsertPreference]);

  const handleResetZoom = useCallback(() => {
    setXRange({min: undefined, max: undefined});
    if (chartRef.current) {
      chartRef.current.resetZoom();
      setIsZoomed(false);
      // Force chart update to restore Y-axis auto-scaling
      chartRef.current.update();
    }
  }, []);

  const handleReset = async () => {
    setIsRefreshing(true);
    try {
      // Reset to 24h range
      setSelectedRange(timeRanges[0]);
      setDateRange({
        start: new Date(Date.now() - 24 * 60 * 60 * 1000),
        end: new Date(),
      });
      setShowCustomDateInputs(false);
      // Remove overlay sensor
      setOverlaySensorId(null);
      setOverlaySensorChannel(null);
      setShowOverlayPicker(false);
      // Reset zoom
      if (chartRef.current) {
        chartRef.current.resetZoom();
        setIsZoomed(false);
      }
      setXRange({ min: undefined, max: undefined });
      // Clear Y-axis scale
      setScaleInputs({ yMin: '', yMax: '' });
      setShowScalePanel(false);
      if (userId) {
        await upsertPreference({
          userId,
          preference_key: `graph_scale_${sensorId}`,
          preference_value: '',
        });
      }
      // Reset data series visibility
      setShowBattery(false);
      setShowRSSI(false);
      setShowSNR(false);
      setShowTemp1(true);
      setShowTemp2(true);
      setShowTemperature(true);
      setShowHighAlarms(true);
      setShowLowAlarms(true);
      // Clear live data
      setLiveData([]);
      setShouldFetchLiveData(false);
    } finally {
      setIsRefreshing(false);
    }
  };

  const sensorAlarms = alarmPoints.filter(
    alarm => alarm.sensor_id === sensor?.id
  );
  const highAlarms = sensorAlarms.filter(a => String(a.alarm_type) === '1');
  const lowAlarms = sensorAlarms.filter(a => String(a.alarm_type) === '2');

  const highAlarmDataset: ChartDataset<'scatter', TimeSeriesData[]> = {
    label: 'High Alarm',
    data: showHighAlarms ? highAlarms.map(a => ({ x: adjustTimestampForDisplay(a.activated), y: a.alarm_values })) : [],
    pointBackgroundColor: 'red',
    pointBorderColor: 'red',
    pointRadius: 6,
    type: 'scatter',
    showLine: false,
    yAxisID: 'y',
  };
  const lowAlarmDataset: ChartDataset<'scatter', TimeSeriesData[]> = {
    label: 'Low Alarm',
    data: showLowAlarms ? lowAlarms.map(a => ({ x: adjustTimestampForDisplay(a.activated), y: a.alarm_values })) : [],
    pointBackgroundColor: 'blue',
    pointBorderColor: 'blue',
    pointRadius: 6,
    type: 'scatter',
    showLine: false,
    yAxisID: 'y',
  };

  // Parse numeric value from API string (e.g. "-60dBm", "12dB")
  const parseSignalValue = (val: string | null | undefined): number | null => {
    if (val == null || val === '') return null;
    const n = parseInt(String(val).replace(/[^\d-]/g, ''), 10);
    return Number.isNaN(n) ? null : n;
  };

  // Smoothed temperature series memoised once so both the line dataset and the
  // missing-data gap markers operate on identical points.
  const temp1Series = useMemo(() => {
    if (!readings) return [] as { x: Date; y: number }[];
    return smoothTemperatureData(
      readings
        .filter(r => r.temperature !== null && r.temperature !== 8001 && r.temperature !== 8002)
        .map(r => ({ x: adjustTimestampForDisplay(r.timestamp), y: r.temperature as number }))
    );
  }, [readings]);

  const temp2Series = useMemo(() => {
    if (!readings) return [] as { x: Date; y: number }[];
    if (sensor.type !== '5' && sensor.type !== '6') return [];
    return smoothTemperatureData(
      readings
        .filter(r => typeof r.temperature_2 === 'number' && r.temperature_2 !== null && !isNaN(r.temperature_2))
        .map(r => ({ x: adjustTimestampForDisplay(r.timestamp), y: r.temperature_2 as number }))
    );
  }, [readings, sensor.type]);

  // Threshold for what counts as "missing data" — adapts to the per-series sample
  // cadence so bucketed views (e.g. 7d → 2h buckets) don't flag every segment.
  const temp1GapThreshold = useMemo(() => inferGapThresholdMs(temp1Series), [temp1Series]);
  const temp2GapThreshold = useMemo(() => inferGapThresholdMs(temp2Series), [temp2Series]);
  const temp1SegmentStyle = useMemo(() => makeGapSegmentStyle(temp1GapThreshold), [temp1GapThreshold]);
  const temp2SegmentStyle = useMemo(() => makeGapSegmentStyle(temp2GapThreshold), [temp2GapThreshold]);
  const temp1GapMarkers = useMemo(() => buildGapMarkers(temp1Series, temp1GapThreshold), [temp1Series, temp1GapThreshold]);
  const temp2GapMarkers = useMemo(() => buildGapMarkers(temp2Series, temp2GapThreshold), [temp2Series, temp2GapThreshold]);

  // Bucketed responses (>25h) have sample_count > 1; the SQL already averages Battery per bucket
  const isBucketed = (readings?.length ?? 0) > 0 && (readings![0].sample_count ?? 1) > 1;

  // Compute datasets and options for direct mutation
  const datasets = useMemo(() => [
    ...(showTemperature && showTemp1 ? [
    {
      label: 'Temperature (°C)',
      data: temp1Series,
      borderColor: 'rgb(75, 192, 192)',
      tension: temp1ChartTension,
      borderWidth: 1.5,
      pointRadius: adaptivePointRadius,
      pointBackgroundColor: 'rgb(75, 192, 192)',
      pointHitRadius: 10,
      segment: temp1SegmentStyle,
      yAxisID: 'y'
    },
    ...(temp1GapMarkers.length > 0 ? [{
      label: '_gap_markers_temp1',
      data: temp1GapMarkers,
      borderColor: 'transparent',
      backgroundColor: 'transparent',
      pointRadius: 5,
      pointHoverRadius: 7,
      pointStyle: 'rectRot' as const,
      pointBackgroundColor: 'rgba(239, 68, 68, 0.85)',
      pointBorderColor: 'rgb(220, 38, 38)',
      pointBorderWidth: 1,
      pointHitRadius: 16,
      showLine: false,
      yAxisID: 'y',
      order: 0,
    }] : []),
    ...(shouldOverlayLive && typeof liveSensor.temperature === 'number' && showTemperature && showTemp1 && activeChannel !== 2 ? [{
      label: 'Live Temperature',
      data: [{ x: new Date(liveSensor.timestamp), y: liveSensor.temperature }],
      borderColor: 'rgba(75,192,192,0.5)',
      backgroundColor: 'rgba(75,192,192,1)',
      pointRadius: 6,
      pointStyle: 'rectRot',
      showLine: false,
      yAxisID: 'y',
      order: 99
    }] : [])
    ] : []),
    ...((sensor.type === '5' || sensor.type === '6') && showTemperature && showTemp2 && activeChannel === null ? [{
      label: 'Temperature 2 (°C)',
      data: temp2Series,
      borderColor: 'rgb(255, 99, 132)',
      tension: temp2ChartTension,
      borderWidth: 1.5,
      pointRadius: adaptivePointRadius,
      pointBackgroundColor: 'rgb(255, 99, 132)',
      pointHitRadius: 10,
      segment: temp2SegmentStyle,
      yAxisID: 'y'
    },
    ...(temp2GapMarkers.length > 0 ? [{
      label: '_gap_markers_temp2',
      data: temp2GapMarkers,
      borderColor: 'transparent',
      backgroundColor: 'transparent',
      pointRadius: 5,
      pointHoverRadius: 7,
      pointStyle: 'rectRot' as const,
      pointBackgroundColor: 'rgba(239, 68, 68, 0.85)',
      pointBorderColor: 'rgb(220, 38, 38)',
      pointBorderWidth: 1,
      pointHitRadius: 16,
      showLine: false,
      yAxisID: 'y',
      order: 0,
    }] : []),
    ...(shouldOverlayLive && typeof liveSensor.temperature2 === 'number' && showTemperature && showTemp2 && activeChannel === null ? [{
      label: 'Live Temperature 2',
      data: [{ x: new Date(liveSensor.timestamp), y: liveSensor.temperature2 }],
      borderColor: 'rgba(255,99,132,0.5)',
      backgroundColor: 'rgba(255,99,132,1)',
      pointRadius: 6,
      pointStyle: 'triangle',
      showLine: false,
      yAxisID: 'y',
      order: 99
    }] : [])
    ] : []),
    ...(sensor.type === '3' && showTemperature ? [{
      label: 'Humidity (%RH)',
      data: readings?.filter(reading => 
        reading.temperature !== 8001 && reading.temperature !== 8002
      ).map(reading => ({
        x: adjustTimestampForDisplay(reading.timestamp),
        y: reading.hum || 0
      })) || [],
      borderColor: 'rgb(192, 75, 192)',
      tension: 0.3,
      borderWidth: 1.5,
      pointRadius: 0,
      pointHitRadius: 10,
      yAxisID: 'y1'
    }] : []),
    ...(showBattery ? [{
      label: 'Battery (V)',
      data: (() => {
        if (!readings) return [];
        const pts = readings
          .filter(r => r.battery >= 0.5)
          .map(r => ({ x: adjustTimestampForDisplay(r.timestamp), y: r.battery }));
        return isBucketed ? pts : smoothBatteryData(pts);
      })(),
      borderColor: 'rgb(255, 159, 64)',
      tension: 0.3,
      borderWidth: 1.5,
      pointRadius: 0,
      pointHitRadius: 10,
      yAxisID: 'y2'
    },
    ...(shouldOverlayLive && typeof liveSensor.Battery === 'string' && showBattery ? [{
      label: 'Live Battery',
      data: [{ x: new Date(liveSensor.timestamp), y: parseFloat(liveSensor.Battery) }],
      borderColor: 'rgba(255,159,64,0.5)',
      backgroundColor: 'rgba(255,159,64,1)',
      pointRadius: 6,
      pointStyle: 'star',
      showLine: false,
      yAxisID: 'y2',
      order: 99
    }] : [])
    ] : []),
    ...(showRSSI ? [{
      label: 'RSSI (dBm)',
      data: readings?.filter(r => r.rssi != null && !Number.isNaN(r.rssi)).map(reading => ({
        x: adjustTimestampForDisplay(reading.timestamp),
        y: reading.rssi!
      })) ?? [],
      borderColor: 'rgb(34, 197, 94)',
      tension: 0.3,
      borderWidth: 1.5,
      pointRadius: 0,
      pointHitRadius: 10,
      yAxisID: 'y3'
    },
    ...(shouldOverlayLive && liveSensor?.RSSI != null && parseSignalValue(liveSensor.RSSI) != null ? [{
      label: 'Live RSSI',
      data: [{ x: new Date(liveSensor.timestamp), y: parseSignalValue(liveSensor.RSSI)! }],
      borderColor: 'rgba(34,197,94,0.5)',
      backgroundColor: 'rgba(34,197,94,1)',
      pointRadius: 6,
      pointStyle: 'rect',
      showLine: false,
      yAxisID: 'y3',
      order: 99
    }] : [])] : []),
    ...(showSNR ? [{
      label: 'SNR (dB)',
      data: readings?.filter(r => r.snr != null && !Number.isNaN(r.snr)).map(reading => ({
        x: adjustTimestampForDisplay(reading.timestamp),
        y: reading.snr!
      })) ?? [],
      borderColor: 'rgb(139, 92, 246)',
      tension: 0.3,
      borderWidth: 1.5,
      pointRadius: 0,
      pointHitRadius: 10,
      yAxisID: 'y4'
    },
    ...(shouldOverlayLive && liveSensor?.SNR != null && parseSignalValue(liveSensor.SNR) != null ? [{
      label: 'Live SNR',
      data: [{ x: new Date(liveSensor.timestamp), y: parseSignalValue(liveSensor.SNR)! }],
      borderColor: 'rgba(139,92,246,0.5)',
      backgroundColor: 'rgba(139,92,246,1)',
      pointRadius: 6,
      pointStyle: 'circle',
      showLine: false,
      yAxisID: 'y4',
      order: 99
    }] : [])] : []),
    ...(overlaySensor && overlayReadings ? [{
      label: `${overlaySensorDisplayName} (overlay °C)`,
      data: smoothTemperatureData(
        overlayReadings
          .filter(r => r.temperature !== null && r.temperature !== 8001 && r.temperature !== 8002)
          .map(r => ({ x: adjustTimestampForDisplay(r.timestamp), y: r.temperature as number }))
      ),
      borderColor: 'rgb(251, 146, 60)',
      borderDash: [6, 3],
      tension: 0.3,
      borderWidth: 1.5,
      pointRadius: 0,
      pointHitRadius: 10,
      yAxisID: 'y',
    }] : []),
  ], [readings, isBucketed, overlayReadings, overlaySensor, overlaySensorDisplayName, shouldOverlayLive, liveSensor, sensor.type, showTemp1, showTemp2, showTemperature, showBattery, showRSSI, showSNR, temp1Series, temp2Series, temp1GapMarkers, temp2GapMarkers, temp1SegmentStyle, temp2SegmentStyle, temp1ChartTension, temp2ChartTension, activeChannel]);

  // Compute options for direct mutation
  const chartOptions = useMemo(() => {
    const displayedHours = (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 3600);
    const baseOptions = getChartOptions({
      sensorName: displayName,
      selectedRangeHours: displayedHours,
      onZoomChange: setIsZoomed,
      showHumidity: sensor.type === '3' && showTemperature,
      showBattery,
      showTemperature,
      showRSSI,
      showSNR
    });
    
    // Calculate proper scaling for the chart based on readings data (both channels)
    const ch1Data = readings ? readings
      .filter(reading =>
        reading.temperature !== null &&
        reading.temperature !== 8001 &&
        reading.temperature !== 8002 &&
        typeof reading.temperature === 'number'
      )
      .map(reading => reading.temperature as number) : [];

    const ch2Data = (sensor.type === '5' || sensor.type === '6') && readings ? readings
      .filter(reading => typeof reading.temperature_2 === 'number' && !isNaN(reading.temperature_2 as number))
      .map(reading => reading.temperature_2 as number) : [];

    const overlayData = overlayReadings
      ? overlayReadings
          .filter(r => r.temperature !== null && r.temperature !== 8001 && r.temperature !== 8002 && typeof r.temperature === 'number')
          .map(r => r.temperature as number)
      : [];

    const allData = [...ch1Data, ...ch2Data, ...overlayData];

    let yMin: number | undefined = undefined;
    let yMax: number | undefined = undefined;

    if (storedScale) {
      yMin = storedScale.yMin;
      yMax = storedScale.yMax;
    } else if (allData.length > 0) {
      // Include alarm thresholds so threshold annotation lines are always in view
      const thresholdVals = [alarmSetpoints.high, alarmSetpoints.low].filter((v): v is number => v != null);
      const allValues = [...allData, ...thresholdVals];
      const dataMin = Math.min(...allValues);
      const dataMax = Math.max(...allValues);
      const range = dataMax - dataMin;
      const padding = Math.max(range * 0.15, 2);
      yMin = dataMin - padding;
      yMax = dataMax + padding;
    }

    return {
      ...baseOptions,
      scales: {
        ...baseOptions.scales,
        x: {
          ...baseOptions.scales?.x,
          min: dateRange.start.getTime(),
          max: dateRange.end.getTime(),
        },
        y: {
          ...baseOptions.scales?.y,
          min: yMin,
          max: yMax,
        }
      },
      plugins: {
        ...baseOptions.plugins,
        zoom: {
          zoom: {
            ...baseOptions.plugins?.zoom?.zoom,
            onZoom: ({chart}: any) => {
              setXRange({min: chart.scales['x'].min, max: chart.scales['x'].max});
              setIsZoomed(true);
            },
            onZoomComplete: ({chart}: any) => {
              const newMin = chart.scales['x'].min;
              const newMax = chart.scales['x'].max;
              if (zoomRefetchTimerRef.current) clearTimeout(zoomRefetchTimerRef.current);
              zoomRefetchTimerRef.current = setTimeout(() => {
                zoomStateRef.current = null;
                setIsZoomed(false);
                setXRange({ min: undefined, max: undefined });
                // Avoid isRangeChanging here — it unmounts the chart and breaks mobile pinch-zoom
                setDateRange({ start: new Date(newMin), end: new Date(newMax) });
              }, 400);
            },
          },
          pan: {
            ...baseOptions.plugins?.zoom?.pan,
            onPan: ({chart}: any) => {
              setXRange({min: chart.scales['x'].min, max: chart.scales['x'].max});
              setIsZoomed(true);
            },
            onPanComplete: ({chart}: any) => {
              const newMin = chart.scales['x'].min;
              const newMax = chart.scales['x'].max;
              if (zoomRefetchTimerRef.current) clearTimeout(zoomRefetchTimerRef.current);
              zoomRefetchTimerRef.current = setTimeout(() => {
                zoomStateRef.current = null;
                setIsZoomed(false);
                setXRange({ min: undefined, max: undefined });
                setDateRange({ start: new Date(newMin), end: new Date(newMax) });
              }, 400);
            },
          },
        },
        annotation: {
          annotations: {}
        },
        tooltip: {
          ...(baseOptions.plugins as any)?.tooltip,
          filter: (item: any) => {
            // Hide internal helper datasets (bands) from the tooltip; gap markers
            // are kept so users can hover them to read the missing-data duration.
            const label: string = item?.dataset?.label ?? '';
            if (label === '_band_min' || label === '_band_max') return false;
            return true;
          },
          callbacks: {
            title: (items: any[]) => {
              const x = items?.[0]?.parsed?.x ?? items?.[0]?.parsed;
              const t = typeof x === 'number' ? x : null;
              if (t == null || Number.isNaN(t)) return '';
              try {
                return formatFns(new Date(t), 'PP — HH:mm');
              } catch {
                return '';
              }
            },
            label: (item: any) => {
              const label: string = item?.dataset?.label ?? '';
              if (label.startsWith('_gap_markers')) {
                const gapMs = (item?.raw as any)?.gapMs;
                if (typeof gapMs === 'number') {
                  return `Missing data: ${formatGapDuration(gapMs)}`;
                }
                return 'Missing data';
              }
              const y = item?.parsed?.y;
              if (y == null) return label;
              return label ? `${label}: ${y}` : `${y}`;
            },
          },
        },
      }
    };
  }, [displayName, sensor.type, dateRange, readings, overlayReadings, showBattery, showTemperature, showRSSI, showSNR, alarmSetpoints, storedScale]);

  // Update chart annotations and alarm datasets when alarm visibility changes
  useEffect(() => {
    if (!chartRef.current || !isChartReady) return;
    
    // Get current zoom state BEFORE making any changes - read directly from scale min/max
    const xScale = chartRef.current.scales['x'];
    
    // Try multiple ways to get the zoom state
    const currentMin = xScale?.min;
    const currentMax = xScale?.max;
    const scaleOptionsMin = (xScale as any)?.options?.min;
    const scaleOptionsMax = (xScale as any)?.options?.max;
    
    // Also try the chart zoom state
    const chartZoomState = (chartRef.current as any)?.zoom;
    const zoomValues = chartZoomState?.getZoom?.('x');
    
    // Update annotations
    if (chartRef.current.options.plugins?.annotation) {
      const pluginOptions = chartRef.current.options.plugins.annotation;
      if (pluginOptions && typeof pluginOptions === 'object' && 'annotations' in pluginOptions) {
        (pluginOptions as any).annotations = {
          ...(alarmSetpoints.high != null && showHighAlarms && {
            highLine: {
              type: 'line' as const,
              adjustScaleRange: false,
              yMin: alarmSetpoints.high,
              yMax: alarmSetpoints.high,
              borderColor: 'red',
              borderWidth: 2,
              label: {
                content: 'High Alarm',
                enabled: true,
                position: 'end' as const
              }
            }
          }),
          ...(alarmSetpoints.low != null && showLowAlarms && {
            lowLine: {
              type: 'line' as const,
              adjustScaleRange: false,
              yMin: alarmSetpoints.low,
              yMax: alarmSetpoints.low,
              borderColor: 'blue',
              borderWidth: 2,
              label: {
                content: 'Low Alarm',
                enabled: true,
                position: 'end' as const
              }
            }
          })
        };
      }
    }
    
    // Update alarm datasets
    const chartData = chartRef.current.data;
    if (chartData && Array.isArray(chartData.datasets)) {
      // Remove old alarm datasets
      chartData.datasets = chartData.datasets.filter(ds => 
        ds.label !== 'High Alarm' && ds.label !== 'Low Alarm'
      );
      
      // Add back alarm datasets based on visibility
      if (showHighAlarms) {
        chartData.datasets.push(highAlarmDataset as any);
      }
      if (showLowAlarms) {
        chartData.datasets.push(lowAlarmDataset as any);
      }
    }
    
    // Update chart WITHOUT animation
    chartRef.current.update('none');
    
    // Restore zoom state AFTER update using currentMin/currentMax from scale
    if (currentMin !== undefined && currentMax !== undefined) {
      // Use a longer delay to ensure chart has fully processed the update
      setTimeout(() => {
        if (chartRef.current) {
          const xScale = chartRef.current.scales['x'];
          if (xScale && (xScale.min !== currentMin || xScale.max !== currentMax)) {
            xScale.options.min = currentMin;
            xScale.options.max = currentMax;
            xScale.min = currentMin;
            xScale.max = currentMax;
            chartRef.current.update('none');
          }
        }
      }, 100);
    }
  }, [showHighAlarms, showLowAlarms, alarmSetpoints, highAlarmDataset, lowAlarmDataset, isChartReady, showThresholdSliders]);

  // Real-time slider preview: update annotation lines as the user drags
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!chartRef.current || !isChartReady || !showThresholdSliders) return;
    const pluginOptions = chartRef.current.options.plugins?.annotation;
    if (!pluginOptions || typeof pluginOptions !== 'object' || !('annotations' in pluginOptions)) return;
    const annotations: Record<string, any> = (pluginOptions as any).annotations ?? {};
    if (showHighAlarms) {
      if (draftHigh !== null) {
        annotations.highLine = {
          type: 'line',
          adjustScaleRange: false,
          yMin: draftHigh,
          yMax: draftHigh,
          borderColor: 'red',
          borderWidth: 2,
          label: { content: `High: ${draftHigh.toFixed(1)}°C`, enabled: true, position: 'end' as const }
        };
      } else {
        delete annotations.highLine;
      }
    }
    if (showLowAlarms) {
      if (draftLow !== null) {
        annotations.lowLine = {
          type: 'line',
          adjustScaleRange: false,
          yMin: draftLow,
          yMax: draftLow,
          borderColor: 'blue',
          borderWidth: 2,
          label: { content: `Low: ${draftLow.toFixed(1)}°C`, enabled: true, position: 'end' as const }
        };
      } else {
        delete annotations.lowLine;
      }
    }
    (pluginOptions as any).annotations = annotations;
    chartRef.current.update('none');
  }, [draftHigh, draftLow, showThresholdSliders, showHighAlarms, showLowAlarms, isChartReady]);

  const handleToggleSliders = () => {
    if (!showThresholdSliders) {
      setDraftHigh(alarmSetpoints.high ?? null);
      setDraftLow(alarmSetpoints.low ?? null);
      setThresholdSaveStatus('idle');
    }
    setShowThresholdSliders(v => !v);
  };

  const handleSaveThresholds = async () => {
    if (!sensor) return;
    const existingAlarm = alarms?.find((a: any) => String(a.sensor_id) === String(sensor.id));
    setThresholdSaveStatus('saving');
    try {
      await updateSensorAlarm({
        sensorId: String(sensor.id),
        settings: { ...existingAlarm, high_alarm: draftHigh != null ? Math.round(draftHigh) : null, low_alarm: draftLow != null ? Math.round(draftLow) : null } as any,
      }).unwrap();
      dispatch(alarmApi.util.invalidateTags(['Alarms']));
      setThresholdSaveStatus('saved');
      setTimeout(() => setThresholdSaveStatus('idle'), 2000);
    } catch {
      setThresholdSaveStatus('error');
      setTimeout(() => setThresholdSaveStatus('idle'), 3000);
    }
  };

  const handleAcknowledge = async () => {
    if (!activeAlarm) return;
    const trimmedNote = ackNoteDraft.trim();
    if (!trimmedNote) {
      setAckError('Please enter a note before acknowledging.');
      return;
    }
    const eventId = activeAlarm.event_id;
    const logsToAck = (activeAlarmLog ?? alarmLog ?? []).filter(
      (log: any) => log.event_id === eventId && !log.acknowledged
    );
    const noteWithUser = `${trimmedNote} —${userDisplayName}`;
    setAckSubmitting(true);
    setAckError(null);
    try {
      await Promise.all(
        logsToAck.map((l: any) =>
          acknowledgeAlarmLog({
            logId: l.idAlarmLog,
            body: {
              acknowledged: true,
              acknowledged_by: userDisplayName,
              alarm_reset: new Date().toISOString(),
              notes: noteWithUser,
            },
          })
        )
      );
      await logAuditEvent(
        AUDIT_ACTIONS.ALARM_ACKNOWLEDGE,
        `Alarm acknowledged from Graph - Event ID: ${eventId}, Sensor ID: ${sensor.id}, Sensor Name: ${sensor.name}, Alarm Type: ${activeAlarm.alarm_type}, Acknowledged by: ${userDisplayName}`,
        user
      );
      dispatch(alarmApi.util.invalidateTags(['Alarms', 'AlarmLog']));
      setAckDialogOpen(false);
      setAckNoteDraft('');
    } catch {
      setAckError('Could not acknowledge alarm. Please try again.');
    } finally {
      setAckSubmitting(false);
    }
  };

  return (
    <div className="p-2 sm:p-6">
      <div className="mb-4 sm:mb-6">
        <button
          onClick={() => fromAlarmLog ? navigate('/alarm-log') : navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          {fromAlarmLog ? 'Back to Alarm Log' : 'Back to Dashboard'}
        </button>
      </div>

      <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow p-3 sm:p-6">
        <div className="mb-4 sm:mb-6">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {displayName}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Serial: {formatSensorSerial(sensor.serialNo, sensor.type)}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {prevSensorId !== null && nextSensorId !== null && (
                <div className="flex items-center gap-0.5 rounded border border-gray-300 dark:border-gray-600 px-1 py-0.5">
                  <button
                    onClick={() => navigate(`/sensors/${prevSensorId}`)}
                    title="Previous sensor graph"
                    className="p-1 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums px-1 whitespace-nowrap">
                    {sensorNavIndex + 1} / {orderedSensorIds.length}
                  </span>
                  <button
                    onClick={() => navigate(`/sensors/${nextSensorId}`)}
                    title="Next sensor graph"
                    className="p-1 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
              <button
                onClick={() => navigate('/alarm-log')}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap"
              >
                <ClipboardList className="h-3.5 w-3.5" />
                Alarm Log
              </button>
              {sensor.zone_id && (
                <button
                  onClick={() => navigate(`/dashboard/plan?zone=${sensor.zone_id}&highlight=${sensor.id}`)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  View on Plan
                </button>
              )}
              {(sensor.type === '5' || sensor.type === '6') && user?.role === 'admin' && (
                <button
                  onClick={() => setShowSplitChannelDialog(true)}
                  className="text-xs px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap"
                >
                  Configure Channels
                </button>
              )}
            </div>
          </div>
        </div>

        {activeAlarm && (() => {
          const k = normalizeAlarmType(String(activeAlarm.alarm_type));
          const wrap =
            k === 'high'
              ? 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700'
              : k === 'low'
              ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/20 dark:border-blue-700'
              : k === 'battery'
              ? 'bg-rose-50 border-rose-300 dark:bg-rose-900/20 dark:border-rose-700'
              : k === 'missing_data'
              ? 'bg-violet-50 border-violet-300 dark:bg-violet-900/20 dark:border-violet-700'
              : 'bg-amber-50 border-amber-300 dark:bg-amber-900/20 dark:border-amber-700';
          const iconCls =
            k === 'high'
              ? 'text-red-600 dark:text-red-400'
              : k === 'low'
              ? 'text-blue-600 dark:text-blue-400'
              : k === 'battery'
              ? 'text-rose-600 dark:text-rose-400'
              : k === 'missing_data'
              ? 'text-violet-600 dark:text-violet-400'
              : 'text-amber-600 dark:text-amber-400';
          const titleCls =
            k === 'high'
              ? 'text-red-700 dark:text-red-300'
              : k === 'low'
              ? 'text-blue-700 dark:text-blue-300'
              : k === 'battery'
              ? 'text-rose-800 dark:text-rose-300'
              : k === 'missing_data'
              ? 'text-violet-800 dark:text-violet-300'
              : 'text-amber-800 dark:text-amber-300';
          return (
          <div className={`mb-4 flex items-center justify-between gap-3 p-3 rounded-lg border ${wrap}`}>
            <div className="flex items-center gap-2 min-w-0">
              <BellRing className={`h-5 w-5 flex-shrink-0 ${iconCls}`} />
              <span className={`text-sm font-semibold ${titleCls}`}>
                {alarmTypeShortLabel(String(activeAlarm.alarm_type))} alarm active
              </span>
              {activeAlarm.alarm_values != null && (
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  — {formatAlarmReading(String(activeAlarm.alarm_type), activeAlarm.alarm_values)}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setAckNoteDraft(''); setAckError(null); setAckDialogOpen(true); }}
              className="flex-shrink-0 px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
            >
              Acknowledge
            </button>
          </div>
          );
        })()}


        <div className="mb-4 sm:mb-6 flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
            {timeRanges.map((range) => (
              <button
                key={range.label}
                onClick={() => handleRangeChange(range)}
                disabled={isRangeChanging}
                className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm flex items-center ${
                  selectedRange === range
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                } ${isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isRangeChanging && selectedRange === range && (
                  <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 animate-spin" />
                )}
                {range.label}
              </button>
            ))}
          </div>

          {showCustomDateInputs && (
            <div className="flex flex-col gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-md sm:flex-row sm:flex-wrap sm:items-end">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 shrink-0">From</span>
                <input
                  type="date"
                  aria-label="Custom range start date"
                  value={toDateInputValue(customDateRange.start)}
                  max={toDateInputValue(customDateRange.end)}
                  onChange={(e) => {
                    const parsed = combineLocalDateTime(e.target.value, toTimeInputValue(customDateRange.start));
                    if (parsed) applyCustomStart(parsed);
                  }}
                  className="px-2 py-1 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded bg-app-panel dark:bg-gray-800 text-gray-900 dark:text-white"
                />
                <input
                  type="time"
                  step={60}
                  aria-label="Custom range start time"
                  value={toTimeInputValue(customDateRange.start)}
                  max={
                    sameLocalCalendarDay(customDateRange.start, customDateRange.end)
                      ? toTimeInputValue(customDateRange.end)
                      : undefined
                  }
                  onChange={(e) => {
                    const parsed = combineLocalDateTime(toDateInputValue(customDateRange.start), e.target.value);
                    if (parsed) applyCustomStart(parsed);
                  }}
                  className="min-w-[6.25rem] px-2 py-1 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded bg-app-panel dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 shrink-0">To</span>
                <input
                  type="date"
                  aria-label="Custom range end date"
                  value={toDateInputValue(customDateRange.end)}
                  min={toDateInputValue(customDateRange.start)}
                  onChange={(e) => {
                    const parsed = combineLocalDateTime(e.target.value, toTimeInputValue(customDateRange.end));
                    if (parsed) applyCustomEnd(parsed);
                  }}
                  className="px-2 py-1 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded bg-app-panel dark:bg-gray-800 text-gray-900 dark:text-white"
                />
                <input
                  type="time"
                  step={60}
                  aria-label="Custom range end time"
                  value={toTimeInputValue(customDateRange.end)}
                  min={
                    sameLocalCalendarDay(customDateRange.start, customDateRange.end)
                      ? toTimeInputValue(customDateRange.start)
                      : undefined
                  }
                  onChange={(e) => {
                    const parsed = combineLocalDateTime(toDateInputValue(customDateRange.end), e.target.value);
                    if (parsed) applyCustomEnd(parsed);
                  }}
                  className="min-w-[6.25rem] px-2 py-1 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded bg-app-panel dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
            <label className="flex items-center text-xs sm:text-sm gap-1">
              <input
                type="checkbox"
                checked={showTemperature}
                onChange={() => setShowTemperature(v => !v)}
                className="form-checkbox rounded text-indigo-600"
              />
              Temperature
            </label>
            {(sensor.type === '5' || sensor.type === '6') && showTemperature && activeChannel === null && (
              <>
                <label className="flex items-center text-xs sm:text-sm gap-1">
                  <input
                    type="checkbox"
                    checked={showTemp1}
                    onChange={() => setShowTemp1(v => !v)}
                    className="form-checkbox rounded text-indigo-600"
                  />
                  Channel 1
                </label>
                <label className="flex items-center text-xs sm:text-sm gap-1">
                  <input
                    type="checkbox"
                    checked={showTemp2}
                    onChange={() => setShowTemp2(v => !v)}
                    className="form-checkbox rounded text-pink-500"
                  />
                  Channel 2
                </label>
              </>
            )}
            {/* Only show Battery button if not type 4 or 5 */}
            {sensor.type !== '4' && sensor.type !== '5' && (
              <button
                onClick={() => setShowBattery(!showBattery)}
                disabled={isRangeChanging}
                className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm flex items-center ${
                  showBattery
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                } ${isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Battery className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                Battery
              </button>
            )}
            <button
              onClick={() => setShowRSSI(!showRSSI)}
              disabled={isRangeChanging}
              className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm ${
                showRSSI
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              } ${isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              RSSI
            </button>
            <button
              onClick={() => setShowSNR(!showSNR)}
              disabled={isRangeChanging}
              className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm ${
                showSNR
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              } ${isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              SNR
            </button>
            {overlaySensor ? (
              <span className="flex items-center gap-1 px-2 py-1 rounded-md text-xs sm:text-sm bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-300 dark:border-orange-700">
                <span className="w-3 h-0.5 bg-orange-400 inline-block" style={{borderTop: '2px dashed rgb(251,146,60)', display:'inline-block', width:'12px', height:'0'}} />
                {overlaySensorDisplayName}
                <button
                  onClick={() => { setOverlaySensorId(null); setOverlaySensorChannel(null); setShowOverlayPicker(false); }}
                  className="ml-1 text-orange-500 hover:text-orange-700 dark:hover:text-orange-200"
                  title="Remove overlay"
                >×</button>
              </span>
            ) : (
              <button
                onClick={() => setShowOverlayPicker(v => !v)}
                className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm ${
                  showOverlayPicker
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                title="Overlay another sensor"
              >
                + Overlay
              </button>
            )}
            <button
              onClick={() => setShowScalePanel(v => !v)}
              className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm ${
                storedScale
                  ? 'bg-amber-500 text-white hover:bg-amber-600'
                  : showScalePanel
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              title={storedScale ? `Custom scale: ${storedScale.yMin}°C to ${storedScale.yMax}°C` : 'Set Y-axis scale'}
            >
              Scale{storedScale ? ' ✓' : ''}
            </button>
            {isZoomed && (
              <button
                onClick={handleResetZoom}
                disabled={isRangeChanging}
                className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 ${
                  isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Reset Zoom
              </button>
            )}
            {selectedRange.hours !== Infinity && selectedRange.hours !== 'custom' && (
              <>
                <button
                  onClick={() => moveTimeWindow('backward')}
                  disabled={isRangeChanging}
                  className={`p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title="Previous period"
                >
                  <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => moveTimeWindow('forward')}
                  disabled={isRangeChanging || dateRange.end >= new Date()}
                  className={`p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title="Next period"
                >
                  <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </>
            )}
            <button
              onClick={handleReset}
              disabled={isRefreshing || isRangeChanging}
              className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm flex items-center bg-gray-500 text-white hover:bg-gray-600 ${
                isRefreshing || isRangeChanging ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title="Reset graph to defaults"
            >
              <RefreshCw className={`h-3 w-3 sm:h-4 sm:w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
              Reset
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center text-xs sm:text-sm gap-1">
              <input
                type="checkbox"
                checked={showHighAlarms}
                onChange={() => setShowHighAlarms(v => !v)}
                className="form-checkbox rounded text-red-500"
              />
              High Alarms
            </label>
            <label className="flex items-center text-xs sm:text-sm gap-1">
              <input
                type="checkbox"
                checked={showLowAlarms}
                onChange={() => setShowLowAlarms(v => !v)}
                className="form-checkbox rounded text-blue-500"
              />
              Low Alarms
            </label>
            <button
              onClick={handleToggleSliders}
              className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm ${
                showThresholdSliders
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              title="Drag sliders to set alarm thresholds"
            >
              Set Thresholds
            </button>
          </div>
        </div>
        {showOverlayPicker && !overlaySensor && (
          <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Select sensor to overlay</span>
              <button onClick={() => setShowOverlayPicker(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm">×</button>
            </div>
            <select
              className="block w-full sm:w-72 pl-3 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              value=""
              onChange={e => {
                const val = e.target.value;
                if (!val) return;
                const [idStr, chStr] = val.split(':');
                const id = Number(idStr);
                const ch = chStr ? (Number(chStr) as 1 | 2) : null;
                setOverlaySensorId(id);
                setOverlaySensorChannel(ch);
                setShowOverlayPicker(false);
              }}
            >
              <option value="">— choose a sensor —</option>
              {expandSensors(sensors?.filter(s => s.id !== sensor.id && !s.is_deleted) ?? [])
                .map(s => {
                  const key = isSplitRow(s) ? `${s.id}:${s._channel}` : String(s.id);
                  return <option key={key} value={key}>{s.name}</option>;
                })}
            </select>
          </div>
        )}

        {showScalePanel && (
          <div className="mb-3 flex flex-wrap items-end gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 self-center">Y-axis scale (°C)</span>
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-gray-500 dark:text-gray-400">Min</label>
              <input
                type="number"
                value={scaleInputs.yMin}
                onChange={e => setScaleInputs(prev => ({ ...prev, yMin: e.target.value }))}
                className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                placeholder="e.g. -10"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-gray-500 dark:text-gray-400">Max</label>
              <input
                type="number"
                value={scaleInputs.yMax}
                onChange={e => setScaleInputs(prev => ({ ...prev, yMax: e.target.value }))}
                className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                placeholder="e.g. 30"
              />
            </div>
            <button
              onClick={handleApplyScale}
              disabled={!scaleInputs.yMin || !scaleInputs.yMax || parseFloat(scaleInputs.yMin) >= parseFloat(scaleInputs.yMax)}
              className="px-3 py-1 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Apply
            </button>
            {storedScale && (
              <button
                onClick={handleClearScale}
                className="px-3 py-1 text-sm rounded-md bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Clear
              </button>
            )}
            {scaleInputs.yMin !== '' && scaleInputs.yMax !== '' && parseFloat(scaleInputs.yMin) >= parseFloat(scaleInputs.yMax) && (
              <span className="text-xs text-red-500 dark:text-red-400">Min must be lower than Max</span>
            )}
            <span className="text-xs text-gray-400 dark:text-gray-500">Saved per sensor, per user</span>
          </div>
        )}

        {/* touch-none prevents the browser consuming touch events for page scroll,
            which would otherwise block pinch-zoom and pan on the chart canvas. */}
        <div className="relative -mx-3 sm:mx-0 h-[300px] md:h-[500px] touch-none md:touch-auto">
          <div className="absolute inset-0">
            {isLoading || isRangeChanging ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mb-2" />
                  <span className="text-gray-500 dark:text-gray-400">
                    {isRangeChanging ? 'Updating data...' : 'Loading data...'}
                  </span>
                </div>
              </div>
            ) : isChartReady && readings && readings.length > 0 && datasets.some(dataset => dataset.data.length > 0) ? (
              <Line
                key={`sensor-details-chart-${selectedRange.label}-${selectedRange.hours}`}
                ref={chartRef}
                data={{ datasets: datasets }}
                options={chartOptions}
                redraw={false}
                className="w-full h-full"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <span className="text-gray-500 dark:text-gray-400">
                  {readings && readings.length > 0 ? 'No valid data points in selected range' : 'No data available'}
                </span>
              </div>
            )}
          </div>
          <p className="mt-1 text-center text-[11px] text-gray-400 dark:text-gray-500 select-none md:hidden">
          Drag to pan · Pinch to zoom
        </p>

        {showThresholdSliders && isChartReady && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2 hidden md:flex gap-1 px-1 py-1 bg-white/90 dark:bg-gray-800/90 rounded-l shadow-md" style={{ width: '44px' }}>
              <div className="flex flex-col items-center flex-1">
                <span className="text-[9px] font-bold text-red-500 leading-none">H</span>
                <span className="text-[9px] text-red-500 leading-none mt-0.5 tabular-nums">
                  {draftHigh != null ? Math.round(draftHigh) : '—'}
                </span>
                <input
                  type="range"
                  min={sliderRange.min}
                  max={sliderRange.max}
                  step={1}
                  value={draftHigh ?? sliderRange.max}
                  onChange={e => setDraftHigh(parseInt(e.target.value, 10))}
                  style={{
                    writingMode: 'vertical-lr' as any,
                    direction: 'rtl' as any,
                    height: '90px',
                    width: '18px',
                    cursor: 'pointer',
                    accentColor: '#ef4444',
                    marginTop: '2px',
                  }}
                />
              </div>
              <div className="flex flex-col items-center flex-1">
                <span className="text-[9px] font-bold text-blue-500 leading-none">L</span>
                <span className="text-[9px] text-blue-500 leading-none mt-0.5 tabular-nums">
                  {draftLow != null ? Math.round(draftLow) : '—'}
                </span>
                <input
                  type="range"
                  min={sliderRange.min}
                  max={sliderRange.max}
                  step={1}
                  value={draftLow ?? sliderRange.min}
                  onChange={e => setDraftLow(parseInt(e.target.value, 10))}
                  style={{
                    writingMode: 'vertical-lr' as any,
                    direction: 'rtl' as any,
                    height: '90px',
                    width: '18px',
                    cursor: 'pointer',
                    accentColor: '#3b82f6',
                    marginTop: '2px',
                  }}
                />
              </div>
            </div>
          )}
        </div>
        {/* ── Mobile threshold controls ─────────────────────────────────────────
             Replaces the tiny overlay on small screens with large step buttons
             and a full-width horizontal slider that are easy to tap accurately. */}
        {showThresholdSliders && isChartReady && (
          <div className="md:hidden mt-3 space-y-3">
            {/* High threshold */}
            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-red-600 dark:text-red-400">High Threshold</span>
                <span className="text-2xl font-bold text-red-600 dark:text-red-400 tabular-nums">
                  {draftHigh != null ? `${Math.round(draftHigh)}°C` : '—'}
                </span>
              </div>
              <input
                type="range"
                min={sliderRange.min}
                max={sliderRange.max}
                step={1}
                value={draftHigh ?? sliderRange.max}
                onChange={e => setDraftHigh(parseInt(e.target.value, 10))}
                className="w-full mb-3"
                style={{ accentColor: '#ef4444' }}
              />
              <div className="grid grid-cols-4 gap-2">
                {([-5, -1, 1, 5] as const).map(delta => (
                  <button
                    key={delta}
                    type="button"
                    onPointerDown={() => setDraftHigh(v => {
                      const base = v ?? sliderRange.max;
                      return Math.min(sliderRange.max, Math.max(sliderRange.min, base + delta));
                    })}
                    className="py-3 rounded-md text-sm font-semibold bg-white dark:bg-gray-800 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 active:bg-red-100 dark:active:bg-red-900/40 select-none"
                  >
                    {delta > 0 ? `+${delta}` : delta}°
                  </button>
                ))}
              </div>
            </div>

            {/* Low threshold */}
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">Low Threshold</span>
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400 tabular-nums">
                  {draftLow != null ? `${Math.round(draftLow)}°C` : '—'}
                </span>
              </div>
              <input
                type="range"
                min={sliderRange.min}
                max={sliderRange.max}
                step={1}
                value={draftLow ?? sliderRange.min}
                onChange={e => setDraftLow(parseInt(e.target.value, 10))}
                className="w-full mb-3"
                style={{ accentColor: '#3b82f6' }}
              />
              <div className="grid grid-cols-4 gap-2">
                {([-5, -1, 1, 5] as const).map(delta => (
                  <button
                    key={delta}
                    type="button"
                    onPointerDown={() => setDraftLow(v => {
                      const base = v ?? sliderRange.min;
                      return Math.min(sliderRange.max, Math.max(sliderRange.min, base + delta));
                    })}
                    className="py-3 rounded-md text-sm font-semibold bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 active:bg-blue-100 dark:active:bg-blue-900/40 select-none"
                  >
                    {delta > 0 ? `+${delta}` : delta}°
                  </button>
                ))}
              </div>
            </div>

            {/* Save / Cancel */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowThresholdSliders(false);
                  setDraftHigh(alarmSetpoints.high ?? null);
                  setDraftLow(alarmSetpoints.low ?? null);
                  setThresholdSaveStatus('idle');
                }}
                className="flex-1 py-3 text-sm font-medium rounded-md bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 active:bg-gray-300 dark:active:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveThresholds}
                disabled={thresholdSaveStatus === 'saving'}
                className={`flex-1 py-3 text-sm font-medium rounded-md disabled:opacity-50 ${
                  thresholdSaveStatus === 'saved'
                    ? 'bg-green-600 text-white'
                    : thresholdSaveStatus === 'error'
                    ? 'bg-red-600 text-white'
                    : 'bg-indigo-600 text-white active:bg-indigo-700'
                }`}
              >
                {thresholdSaveStatus === 'saving' ? 'Saving…' : thresholdSaveStatus === 'saved' ? 'Saved!' : thresholdSaveStatus === 'error' ? 'Error – retry?' : 'Save Thresholds'}
              </button>
            </div>
          </div>
        )}

        {showThresholdSliders && (
          <div className="hidden md:flex items-center justify-end gap-2 mt-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              H: {draftHigh != null ? `${Math.round(draftHigh)}°C` : '—'}&nbsp;&nbsp;L: {draftLow != null ? `${Math.round(draftLow)}°C` : '—'}
            </span>
            <button
              onClick={() => {
                setShowThresholdSliders(false);
                setDraftHigh(alarmSetpoints.high ?? null);
                setDraftLow(alarmSetpoints.low ?? null);
                setThresholdSaveStatus('idle');
              }}
              className="px-3 py-1 text-xs rounded-md bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveThresholds}
              disabled={thresholdSaveStatus === 'saving'}
              className={`px-3 py-1 text-xs rounded-md disabled:opacity-50 ${
                thresholdSaveStatus === 'saved'
                  ? 'bg-green-600 text-white'
                  : thresholdSaveStatus === 'error'
                  ? 'bg-red-600 text-white'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {thresholdSaveStatus === 'saving' ? 'Saving…' : thresholdSaveStatus === 'saved' ? 'Saved!' : thresholdSaveStatus === 'error' ? 'Error – retry?' : 'Save Thresholds'}
            </button>
          </div>
        )}
      </div>

      {showSplitChannelDialog && (
        <SplitChannelDialog
          sensor={sensor}
          onClose={() => setShowSplitChannelDialog(false)}
        />
      )}

      <AlarmAcknowledgeDialog
        isOpen={ackDialogOpen}
        onClose={() => { if (!ackSubmitting) { setAckDialogOpen(false); setAckNoteDraft(''); setAckError(null); } }}
        alarmType={String(activeAlarm?.alarm_type ?? '')}
        sensorName={sensor.name}
        serialDisplay={formatSensorSerial(sensor.serialNo, sensor.type)}
        alarmValues={activeAlarm?.alarm_values}
        activatedAt={activeAlarm?.activated}
        alarmResetAt={null}
        eventId={activeAlarm?.event_id}
        noteDraft={ackNoteDraft}
        onNoteChange={setAckNoteDraft}
        onAcknowledge={handleAcknowledge}
        isSubmitting={ackSubmitting}
        error={ackError}
      />
    </div>
  );
}